import moment from 'moment';
import { showMoreText } from '../../ShowMoreText';
import { CONSTANT, CUSTOM_FIELD_TYPE, FORMAT, COLOR,  PROFILE_SECTION, PROFILE_DEFAULT_FIELDS } from '../constant';
import { numFormatter } from './number';

export const hexToRgba = (hex, alpha) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    const bg_color = result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
    if (bg_color) return `rgba(${bg_color.r}, ${bg_color.g}, ${bg_color.b}, ${alpha ? alpha : 1})`;
    return ''
}
export const random = {
    id: (length = 16) => (Math.floor(Math.pow(10, length - 1) + Math.random() * (Math.pow(10, length) - Math.pow(10, length - 1) - 1))).toString(),
    color: () => CONSTANT.COLORS[Math.floor(Math.random() * CONSTANT.COLORS.length)],
    number: (min = 0, max = 100) => Math.floor(Math.random() * (max - min + 1) + min)
};

export const getRandomId = (length = 24) => {
    const hex = '0123456789abcdef';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += hex[Math.floor(Math.random() * 16)];
    }
    return result;
}

// Support-portal button eligibility — owner/org-admin, System Admin (userType 3),
// or Manager (userType 2) with all-employees visibility (employeeVisibility 1).
// Centralised so HRMS/PMS user menus share one rule (UAC codes are platform-wide).
export const isSupportPortalEligible = ({ isAdmin, userType, employeeVisibility } = {}) => {
    const USER_TYPE_SYSTEM_ADMIN = 3;
    const USER_TYPE_MANAGER = 2;
    const VISIBILITY_ALL_EMPLOYEE = 1;
    return !!isAdmin
        || userType === USER_TYPE_SYSTEM_ADMIN
        || (userType === USER_TYPE_MANAGER && employeeVisibility === VISIBILITY_ALL_EMPLOYEE);
};

export const handleError = (set, msg) => {
    set(msg)
    setTimeout(() => set(""), 5000)
}
export const getCustomFieldValue = (value, cfg) => {
    if (value == null || value === '') return '--';
    const opts = cfg?.options || [];

    switch (cfg?.type) {
        case CUSTOM_FIELD_TYPE.DATE:
            return moment(value).format(FORMAT.TICKET_DATE);
        case CUSTOM_FIELD_TYPE.MULTI_SELECT:
            return Array.isArray(value)
                ? value.map(i => opts.find(o => (o?._id || o?.value) === i)?.value || i).join(', ')
                : (value?.toString?.() ?? '--');
        case CUSTOM_FIELD_TYPE.SELECT:
        case CUSTOM_FIELD_TYPE.RADIO:
            return opts.find(o => (o._id || o.value) === value)?.value || (value?.toString?.() || '--');
        case CUSTOM_FIELD_TYPE.TOGGLE:
        case CUSTOM_FIELD_TYPE.CHECKBOX:
            return value ? 'Yes' : 'No';
        case CUSTOM_FIELD_TYPE.TEXT:
        case CUSTOM_FIELD_TYPE.TEXT_AREA:
            return value ? showMoreText(String(value), 25) : '--';
        default:
            return value?.toString?.() ?? '--';
    }
}

// Query-builder field `type` → candidate profile section(s) whose rename config owns the field.
// Multi-candidate (Employee information) resolves per-field by key membership; single-candidate
// sections always route to that section so backend renames apply.
const QB_TYPE_PROFILE_SECTIONS = {
    1: [PROFILE_SECTION.basicInfo, PROFILE_SECTION.contactSocial, PROFILE_SECTION.address, PROFILE_SECTION.education, PROFILE_SECTION.previousEmployee], // Employee information
    2: [PROFILE_SECTION.jobInfo],           // Job Information
    3: [PROFILE_SECTION.compensationInfo],  // Compensation
    5: [PROFILE_SECTION.bankInfo],          // Bank Details
    13: [PROFILE_SECTION.orgStructureInfo], // Org Structure Details
};

const profileSectionOwnsKey = (section, key) =>
    (PROFILE_DEFAULT_FIELDS?.[section] || []).some(f => f?.defaultKey === key);

const resolveProfileSection = (fieldType, key) => {
    const candidates = QB_TYPE_PROFILE_SECTIONS[fieldType] || [];
    if (!candidates.length) return null;
    if (candidates.length === 1) return candidates[0];
    return candidates.find(sec => profileSectionOwnsKey(sec, key)) || null;
};

/**
 * Relabels the static query-builder field sections with the org's renamed labels.
 * `getLabel` comes from useFieldLabel(). Fields whose key isn't renamed (or has no
 * rename config) keep their constant name, since getLabel falls back to the default.
 */
export const applyFieldLabels = (sections = [], getLabel) => {
    if (typeof getLabel !== 'function' || !Array.isArray(sections)) return sections;
    return sections.map(section => ({
        ...section,
        inner_menu: (section?.inner_menu || []).map(field => {
            const profileSection = resolveProfileSection(field?.type, field?.key);
            return profileSection
                ? { ...field, name: getLabel(profileSection, field?.key, field?.name) }
                : field;
        }),
    }));
}

// useHTML chart tooltips / innerHTML sinks must escape any text we inject (tenant names, labels, etc).
export const escapeHtml = (str = '') => String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Module-keyed thresholds + opacity tint. `default` keeps the original
// behavior (33/67 bands, 1A tint); `recruit` uses 40/75 bands with the 0D tint.
const PROGRESS_COLOR_PRESET = {
    default: { low: 33, high: 67, alpha: '1A' },
    recruit: { low: 40, high: 75, alpha: '0D' },
};
export const getProgressColor = (value, opacity = 0, preset = 'default') => {
    const P = PROGRESS_COLOR_PRESET[preset] || PROGRESS_COLOR_PRESET.default;
    if (value >= 0 && Math.floor(value) <= P.low) {
        return opacity ? `#FF6961${P.alpha}` : COLOR.u_red;
    } else if (value >= P.low + 1 && Math.floor(value) <= P.high) {
        return opacity ? `#FFA700${P.alpha}` : COLOR.u_orange;
    } else if (Math.floor(value) >= P.high + 1) {
        return opacity ? `#60B158${P.alpha}` : COLOR.u_green;
    }
    return COLOR.u_green;
};

export const updateFaviconWithBadge = (notificationCount, appName = '') => {
    const favicon = document.querySelector("link[rel~='icon']");
    if (!favicon) return;

    const originalFaviconUrl = favicon.getAttribute('data-original-href') || favicon.href;

    if (!favicon.getAttribute('data-original-href')) {
        favicon.setAttribute('data-original-href', originalFaviconUrl);
    }
    setTimeout(() => {
        let route = document.title?.split("|")
        let sub = route[route?.length - 2];
        let isPMS = appName === 'PMS'
        route = route[route?.length - 1];
        document.title = `${notificationCount ? `(${parseInt(notificationCount) > 99 ? '99+' : notificationCount}) | ` : ''} ${(sub && isPMS) ? `${sub} |` : ""} ${appName ? `Super ${appName}` : ""}`
    }, 500)
    // document.title = `${notificationCount ?  `(${ parseInt(notificationCount) > 99 ? '99+' : notificationCount }) | ` : ''}Superworks - ASSET`;
    if (notificationCount > 0) {
        const img = new Image();
        img.src = originalFaviconUrl;

        img.onload = () => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            canvas.width = img.width + 2;
            canvas.height = img.height + 2;

            context.drawImage(img, 0, 0, canvas.width, canvas.height);
            const radius = 6;
            const x = canvas.width - radius;
            const y = canvas.height - 5;

            context.beginPath();
            context.arc(x, y, radius, 0, 2 * Math.PI);
            context.fillStyle = 'red';
            context.fill();

            context.font = 'bold 10px Arial';
            context.fillStyle = 'white';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText(numFormatter(notificationCount), x, y);

            favicon.href = canvas.toDataURL('image/png');
        };
    } else {
        favicon.href = originalFaviconUrl;
    }
}
// ===== migrated from pms/src/common/functions/common.jsx =====
export const generateIdWithTextNumber = (length = 10) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

export const showCompanyField = (orgId, projectNumber) => {
    const obj = {
        "NLgyWdldGw" : [220, 507, 361, 264],
        "K2zTiIvU6q" : [2, 1]
    }
    return  obj[orgId]?.includes(projectNumber)
}
export const navigateTo = (a, b, c) => {
    const h = a?.push && a, to = h ? b : a, e = (h ? c : b) || window.event;
    const url = typeof to === 'string' ? to : to?.pathname;
    if (url) (e?.ctrlKey || e?.metaKey) ? window.open(url, '_blank', 'noopener,noreferrer') : h ? h.push(to) : window.location.href = url;
};
export const handleMeetingLinkClick = (meetLink) => {
    if (!meetLink) return;

    try {
        let finalLink = meetLink;

        if (!meetLink.startsWith('http')) {
            finalLink = `https://${meetLink}`;
        }

        const newWindow = window.open(finalLink, '_blank', 'noopener,noreferrer');

        if (newWindow) {
            newWindow.opener = null;
        }
    } catch (error) {
        console.error('Error opening meeting link:', error);
        window.open(meetLink, '_blank', 'noopener,noreferrer');
    }
}


Object.defineProperty(String.prototype, 'toCapitalize', {
    value: function () {
        return this.charAt(0).toUpperCase() + this.slice(1).toLowerCase();
    },
    enumerable: false,
    configurable: true
});

export const hexToRgbA = (hex, opacity) => {
    let c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('');
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c = '0x' + c.join('');
        return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + opacity + ')';
    }
    throw new Error('Bad Hex');
}
